import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Card, Icon } from '@/components/ui';

/**
 * ErrorBoundary — last-line-of-defense fallback for catastrophic React errors.
 *
 * Wraps the two most critical pages (/run and /live) so that a crash in a
 * child component (undefined prop, bad type assumption, render loop, …) NEVER
 * produces a white screen. The fallback explains what happened, offers a
 * manual reload, and a way back to the Dashboard.
 *
 * Why a class component : React error boundaries require ``getDerivedStateFromError``
 * + ``componentDidCatch`` which have no hooks equivalent as of React 19.
 *
 * See CLAUDE.md §-1 — robustness is not optional on Run / Live.
 */

export interface ErrorBoundaryProps {
  /** Human label shown in the fallback ("Suivi en direct", "Nouvelle campagne"…). */
  label: string;
  /** Optional back-to-home link (defaults to "/"). */
  homeHref?: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  info: ErrorInfo | null;
  // Bumped by the "Réessayer" button to force React to re-mount the subtree.
  attempt: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, info: null, attempt: 0 };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to console so the operator can open devtools and grab the stack.
    // In production we could also POST to a /api/errors endpoint.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${this.props.label}]`, error, info);
    this.setState({ info });
  }

  private reset = (): void => {
    this.setState((s) => ({ error: null, info: null, attempt: s.attempt + 1 }));
  };

  private reload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error, info, attempt } = this.state;
    if (!error) {
      // `key={attempt}` forces a remount of the subtree on retry — any cached
      // stale state inside children is discarded.
      return <div key={attempt}>{this.props.children}</div>;
    }
    const stack = info?.componentStack ?? error.stack ?? '';
    return (
      <div className="content">
        <Card
          title={<span>Erreur · {this.props.label}</span>}
          subtitle="la page s'est arrêtée sur une exception non gérée"
          actions={
            <div style={{ display: 'flex', gap: 6 }}>
              <Button variant="ghost" size="sm" onClick={this.reset} leftIcon={<Icon name="retry" size={12} />}>
                Réessayer
              </Button>
              <Button variant="ghost" size="sm" onClick={this.reload} leftIcon={<Icon name="refresh" size={12} />}>
                Recharger
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={() => { window.location.href = this.props.homeHref ?? '/'; }}
                leftIcon={<Icon name="chev" size={12} style={{ transform: 'rotate(90deg)' }} />}
              >
                Dashboard
              </Button>
            </div>
          }
        >
          <div
            role="alert"
            style={{
              padding: 14,
              background: 'var(--danger-soft)',
              border: '1px solid color-mix(in oklab, var(--danger) 30%, transparent)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--danger)',
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            <div style={{ fontWeight: 'var(--weight-semibold)', marginBottom: 6 }}>
              {error.name}: {error.message}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              La plateforme reste opérationnelle — seule cette page a crashé. Tu peux
              réessayer (remontage du composant) ou recharger (reset complet).
            </div>
          </div>
          {stack && (
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                Détails techniques (stack)
              </summary>
              <pre
                className="mono"
                style={{
                  fontSize: 11,
                  background: 'var(--surface-0)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: 10,
                  overflowX: 'auto',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  color: 'var(--text-secondary)',
                }}
              >
                {stack.trim()}
              </pre>
            </details>
          )}
        </Card>
      </div>
    );
  }
}
